import { FocusMonitor } from "@angular/cdk/a11y";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnInit,
} from "@angular/core";
import { BaseFieldDirective } from "../base-field.directive";
import { SelectField, SelectFieldItem } from "./select-field";
import { map } from "rxjs/operators";

@Component({
    selector: "ui-select-field",
    templateUrl: "./select-field.component.html",
    styleUrls: ["./select-field.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectFieldComponent extends BaseFieldDirective implements OnInit {
    @Input() public config!: SelectField;
    
    public listaFiltrada: SelectFieldItem[] = [];
    public listaCompleta: SelectFieldItem[] = [];
    public selecionado: string = null;
    private SELECIONE = "Selecione";
    
    constructor(
        protected fmRef: FocusMonitor,
        protected elRef: ElementRef<HTMLElement>,
        protected changeDetector: ChangeDetectorRef,
        protected ngZone: NgZone
    ) {
        super(fmRef, elRef, changeDetector, ngZone);
    }
    
    ngOnInit(): void {
        super.ngOnInit();
        
        if (this.config.useAsync) {
            this.subscriptions.push(
                this.config.valuesAsync?.pipe(
                    map((values) => {
                        this.listaCompleta = values ?? [];
                        this.listaFiltrada = values ?? [];
                        // OnPush: a lista pode chegar depois (ex.: HTTP após abrir o formulário); sem isto as opções só
                        // apareceriam na próxima interação com o campo.
                        this.changeDetector.markForCheck();
                    })
                ).subscribe(),
            );
        } else {
            this.listaCompleta = this.config.values || [];
            this.listaFiltrada = this.listaCompleta;
        }
        
        if (this.config.useValueSelected) {
            this.subscriptions.push(
                this.config.valueSelected.pipe(
                    map((x) => {
                        this.selecionado = x?.id || null;
                        this.controle.setValue(this.selecionado);
                    })
                ).subscribe(),
            );
        } else {
            this.selecionado = this.controle.value || null;
        }
        
        if (this.config.multiple && !this.controle.value) {
            this.controle.setValue([]);
        }
    }
    
    public atualizaControle = (event: any) => {
        if (!this.config?.onValueSelectedFunc) return;
        
        if (this.config.multiple) {
            const values = event.value || [];
            this.config.onValueSelectedFunc(values);
        } else {
            this.config.onValueSelectedFunc(event.value);
        }
    };
    
    getToolTip(data: string) {
        if (!data) return "";
        
        const item = this.listaCompleta.find((x) => x.value === data);
        return item ? item.text : "";
    }
    
    filtrarValores(valor: string) {
        const filtro = valor?.toLowerCase() ?? "";
        const selecionados = new Set(this.controle.value || []);
        
        this.listaFiltrada = this.listaCompleta
            .filter(
                (x) =>
                    x.text.toLowerCase().includes(filtro) ||
                    selecionados.has(x.value),
            )
            .sort((a, b) => {
                const aText = a.text.toLowerCase();
                const bText = b.text.toLowerCase();
                
                const aStarts = aText.startsWith(filtro);
                const bStarts = bText.startsWith(filtro);
                
                if (aStarts && !bStarts) return -1;
                if (!aStarts && bStarts) return 1;
                
                const aIncludes = aText.includes(filtro);
                const bIncludes = bText.includes(filtro);
                
                if (aIncludes && !bIncludes) return -1;
                if (!aIncludes && bIncludes) return 1;
                
                return aText.localeCompare(bText);
            });
    }
    
    isVisivel(option: SelectFieldItem): boolean {
        if (!this.listaFiltrada.length) return true;
        
        return (
            this.listaFiltrada.includes(option) ||
            this.controle.value?.includes(option.value)
        );
    }
    
    filtro(item: SelectFieldItem, value: string) {
        return item.text.toLowerCase().indexOf(value.toLowerCase()) > -1;
    }
    
    todosSelecionados(): boolean {
        return (
            this.config.multiple &&
            this.controle.value?.length === this.listaFiltrada.length
        );
    }
    
    getTooltipAdaptado(): string {
        if (!this.config.multiple) {
            return this.getToolTip(this.controle.value);
        }
        
        const values: string[] = this.controle.value || [];
        return this.listaCompleta
            .filter((x) => values.includes(x.value))
            .map((x) => x.text)
            .join(", ");
    }
    
    limparSelecao(event: Event) {
        event.stopPropagation();
        
        if (this.config.multiple) {
            this.controle.setValue([]);
        } else {
            this.controle.setValue(null);
        }
        
        if (this.config.onValueSelectedFunc) {
            this.config.onValueSelectedFunc(this.controle.value);
        }
    }
    
    get textoResumido(): string {
        const values: string[] = this.controle.value || [];
        
        const textos = this.listaCompleta
            .filter((x) => values.includes(x.value))
            .map((x) => x.text);
        
        if (!textos.length) return this.SELECIONE;
        if (textos.length == 1) return textos[0];
        
        if (textos.length > 1)
            return `${textos.length} Opções Selecionadas`;
    }
}
